import { useState } from 'react'
import { View } from 'react-native'
import styled from 'styled-components'
import {
  useAuthStatusQuery,
  useUpdateProfileMutation,
  useVerifyEmailResendMutation,
} from '@mwxltd/flix-api-client'
import {
  AmendEmailForm,
  VerifyEmailFootnoteContent,
  VerifyEmailMainContent,
} from '@flix/common/domain/membership'
import messages from '@flix/common/domain/membership/messages'
import { Button } from '@flix/common/elements/controls'
import formMessages from '@flix/common/elements/forms/messages'
import compose from '@flix/common/utils/compose'
import { useAppState } from '../../../../containers/app/AppState/AppState'
import { useSnackbar } from '../../../../containers/app/Snackbar/SnackbarContext'
import { ifNotSubscribedGoTo, ifVerifiedGoTo } from '../../../routeGuards/routeGuards'
import { Container } from '../../components/Wizard/Wizard'
import { SUBSCRIBE_WIZARD_PATH } from '../../constants'

const ButtonContainer = styled(View)`
  width: 280px;
`

export const Step3VerifyEmail = ({ history }) => {
  const { openSubsSuccessModal, getAuthSuccessPath, clearAuthSuccessPath } = useAppState()
  const { snackbarMsg, snackbarError } = useSnackbar()

  const {
    refetch: refetchAuthStatus,
    loading: refetchingAuthStatus,
    data: { authStatus } = {},
  } = useAuthStatusQuery({ notifyOnNetworkStatusChange: true })

  const [resendVerificationEmail, { loading: resending }] = useVerifyEmailResendMutation({
    onCompleted: () => snackbarMsg(messages.verifyEmailResendSuccess),
    onError: snackbarError,
  })

  const [amendEmail, setAmendEmail] = useState(false)
  const [updateProfile] = useUpdateProfileMutation({
    onCompleted() {
      snackbarMsg(messages.verifyEmailResendSuccess)
      setAmendEmail(false)
    },
    onError: snackbarError,
  })

  if (!authStatus.hasSignedIn) {
    return null // Components higher up would have handled loading and errors
  }

  const onDoneVerificationPress = () =>
    refetchAuthStatus()
      .then(({ data }) => {
        if (!data?.authStatus.emailVerified) {
          return snackbarMsg(messages.verifyEmailDoneItFailed)
        }

        const authSuccessPath = getAuthSuccessPath()
        if (authSuccessPath) {
          clearAuthSuccessPath()
          history.replace(authSuccessPath)
        } else {
          // We only show this Now Showing modal if there is no defined authSuccessPath, since
          // if the user is taken somewhere else like a group watch, this modal is confusing.
          openSubsSuccessModal()
        }
      })
      .catch(snackbarError)

  return (
    <Container maxWidth="440px">
      <VerifyEmailMainContent email={authStatus.email} />

      {amendEmail && (
        <AmendEmailForm
          email={authStatus.email}
          onPressAmendEmail={updateProfile}
          onCancel={() => setAmendEmail(false)}
        />
      )}
      <ButtonContainer>
        {!amendEmail && (
          <Button
            intlMessage={
              refetchingAuthStatus ? messages.verifyEmailDoneChecking : messages.verifyEmailDoneIt
            }
            onPress={onDoneVerificationPress}
            disabled={refetchingAuthStatus}
            alignSelf="stretch"
          />
        )}
        {!amendEmail && (
          <Button
            intlMessage={formMessages.resendVerification}
            onPress={resendVerificationEmail}
            disabled={resending}
            alignSelf="stretch"
          />
        )}
        {amendEmail ? (
          <Button
            link
            intlMessage={formMessages.cancel}
            onPress={() => setAmendEmail(false)}
            disabled={resending}
          />
        ) : (
          <Button
            intlMessage={messages.verifyEmailReenter}
            onPress={() => setAmendEmail(true)}
            alignSelf="stretch"
          />
        )}
      </ButtonContainer>

      <VerifyEmailFootnoteContent region={authStatus.region} />
    </Container>
  )
}

export default compose(
  ifNotSubscribedGoTo(`${SUBSCRIBE_WIZARD_PATH}/subscribe`),
  ifVerifiedGoTo('/now-showing'),
)(Step3VerifyEmail)
